import { Link } from 'react-router-dom';
import { ArrowRight, Pill } from 'lucide-react';
import { getCountryName } from '@quds/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button, ButtonLink, Card, CardHeader, DemoBadge, EmptyState, ErrorState, LoadingState, NotLinkedNotice } from '@/components/ui';
import { getPharmacyOverview, useQuery } from '@/data';
import { formatNumber, formatRelative } from '@/lib/format';
import { BusinessStatusBadge } from '../pharmacies/components/StatusBadges';

function Stat({ label, value, to }: { label: string; value: number; to: string }) {
  return (
    <Link to={to} className="card group block px-5 py-4 transition-colors hover:border-primary/40 focus-visible:shadow-focus focus-visible:outline-none">
      <p className="text-[13px] text-ink-secondary">{label}</p>
      <p className="mt-1 flex items-center justify-between text-2xl font-semibold tabular-nums text-ink">
        {formatNumber(value)}
        <ArrowRight size={16} aria-hidden="true" className="text-ink-muted transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
      </p>
    </Link>
  );
}

export function OverviewPage() {
  const { data, error, loading, reload } = useQuery(getPharmacyOverview, []);

  return (
    <>
      <PageHeader title="Overview" subtitle="Pharmacy category at a glance" />
      {loading ? (
        <LoadingState />
      ) : error || !data ? (
        <ErrorState message={error?.message ?? 'Unknown error'} action={<Button variant="secondary" onClick={reload}>Try again</Button>} />
      ) : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Stat label="Pharmacies" value={data.total} to="/pharmacies" />
            <Stat label="Active" value={data.byStatus.active} to="/pharmacies?status=active" />
            <Stat label="Onboarding" value={data.byStatus.onboarding} to="/pharmacies?status=onboarding" />
            <Stat label="Suspended" value={data.byStatus.suspended} to="/pharmacies?status=suspended" />
          </div>

          <NotLinkedNotice className="mb-6">
            Online status, license expiry and update health come from production systems and will appear here after production linking.
            {data.demo > 0 && ` ${formatNumber(data.demo)} of ${formatNumber(data.total)} pharmacies are seeded demo records.`}
          </NotLinkedNotice>

          <Card>
            <CardHeader
              title="Recently added"
              description={`${formatNumber(data.branches)} branches across all pharmacies`}
              actions={<ButtonLink to="/pharmacies?sort=createdAt&dir=desc" variant="ghost" size="sm">View all</ButtonLink>}
            />
            {data.recent.length === 0 ? (
              <EmptyState compact icon={<Pill size={20} aria-hidden="true" />} title="No pharmacies yet" description="Pharmacies you add will appear here." />
            ) : (
              <ul className="divide-y divide-line">
                {data.recent.map((p) => (
                  <li key={p.id} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:gap-4">
                    <div className="min-w-0 flex-1">
                      <Link to={`/pharmacies/${p.id}`} className="font-medium text-ink hover:text-primary hover:underline">
                        {p.name}
                      </Link>
                      <p className="flex items-center gap-1.5 text-xs text-ink-muted">
                        {p.code} · {p.city}, {getCountryName(p.country)} · added {formatRelative(p.createdAt)}
                        {p.isDemo && <DemoBadge />}
                      </p>
                    </div>
                    <BusinessStatusBadge status={p.status} />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </>
  );
}
